/**
 * Pure helpers for Merlin's filed brief on the thread workspace (kanban
 * 2ad80767 — Jess, QT-10026: "open with a problem statement, next list a
 * few key facts, do-next actions listed separately, the additional info
 * below that").
 *
 * The brief reads top to bottom: the problem (at_a_glance), key facts (the
 * identifiers, severity, owner and court already on the filing, plus the
 * `key_facts` newer filings write — each fact opening on what Merlin checked
 * for it), do next (one numbered list — the proposed case-file items, then
 * Merlin's recommended action, split into its own items when Merlin filed it
 * as a list), and what is still unconfirmed. The trace and the rest fold away
 * at the bottom of the pane, by the timeline (Jess, 2026-10-01). Older filings
 * render the same.
 */
import { keyLabel } from '../../components/portal/keyLabel';
import { organizeChips } from '../../components/portal/threadTriageView';
import { THREAD_SEVERITY_LABEL } from '../../components/portal/investigationsView';
import { isCustomerDesk, splitDesk } from '../../components/quality/deskSplit';
import { restatedItem, stepOwner } from './stepDedupe';

export { splitDesk };
import type { CaseTaskOwner } from '../../services/qcpInvestigations';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { handoffWords, isDraft, isHandedPassAlong } from '../../components/quality/askKind';
import type {
  ThreadCauseStep,
  ThreadCaseFileProposal,
  ThreadEvidenceRef,
  ThreadInvestigation,
  ThreadKeyFact,
} from '../../services/qcpInvestigations';

/* ---------------------------------------------------------------- */
/* Evidence refs (kanban 1af16b1c)                                   */
/* ---------------------------------------------------------------- */

export interface EvidenceRef {
  ref: string;
  /** One of the filing's kinds, or '' when Merlin gave none it knows. */
  kind: string;
  note: string;
}

/** The kinds the filing schema names; anything else shows as a bare ref. */
export const EVIDENCE_KIND_LABEL: Record<string, string> = {
  order: 'Order',
  batch: 'Batch',
  fill: 'Fill',
  po: 'PO',
  lot: 'Lot',
  sku: 'SKU',
  signal: 'Signal',
  email: 'Email',
  issue: 'Issue',
  case: 'Case',
  customer: 'Customer',
  source: 'Source',
};

/**
 * The refs behind a fact or a step as the brief lists them: trimmed, blank
 * refs dropped, a ref repeated (any case) kept once, `other` and unknown
 * kinds read as no kind. Absent or not a list is none.
 */
export function evidenceRefs(raw: unknown): EvidenceRef[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: EvidenceRef[] = [];
  for (const item of raw as Array<Partial<ThreadEvidenceRef> | string | null>) {
    const ref = (typeof item === 'string' ? item : item?.ref ?? '').trim();
    if (!ref || seen.has(ref.toLowerCase())) continue;
    seen.add(ref.toLowerCase());
    const kindRaw = typeof item === 'string' ? '' : (item?.kind ?? '').trim().toLowerCase();
    out.push({
      ref,
      kind: kindRaw in EVIDENCE_KIND_LABEL ? kindRaw : '',
      note: typeof item === 'string' ? '' : (item?.note ?? '').trim(),
    });
  }
  return out;
}

/** A ref that names one of the thread's loaded signals, by id. */
export function signalForRef(
  ref: EvidenceRef,
  signals: ReadonlyArray<{ id: string; observation: string }>,
): { id: string; observation: string } | null {
  const id = ref.ref.toLowerCase();
  return signals.find((s) => s.id.toLowerCase() === id) ?? null;
}

/* ---------------------------------------------------------------- */
/* Key facts                                                         */
/* ---------------------------------------------------------------- */

export interface KeyFact {
  /** Stable key for React and tests (`severity`, `owner`, `court`, `key:batch`). */
  key: string;
  label: string;
  value: string;
}

/** Only the severities the filing schema allows are labelled; anything
 *  else (a blank or a typo from an older filing) is left out, not shown raw. */
function severityFact(filing: ThreadInvestigation): KeyFact | null {
  const label = (THREAD_SEVERITY_LABEL as Record<string, string | undefined>)[filing.severity];
  return label ? { key: 'severity', label: 'Severity', value: label } : null;
}

/**
 * The key facts row: severity, then the bridge keys in canonical order.
 * `overflow` holds the bridge keys past the cap, behind "+N more".
 */
export function briefKeyFacts(
  filing: ThreadInvestigation,
  // Kept for callers; the proposal's court no longer shows as a chip.
  _proposal: ThreadCaseFileProposal | null,
  bridgeKeyCap = 6,
): { visible: KeyFact[]; overflow: KeyFact[] } {
  // No "Merlin suggests <owner> · N% confident" or "Ball on us" chips: the
  // header already says who it was sent to and where it stands, and both
  // went stale the moment the item was sent on (iteration 6).
  const head = [severityFact(filing)].filter(
    (f): f is KeyFact => f !== null,
  );
  const chips = organizeChips(Object.entries(filing.bridge_keys ?? {}), bridgeKeyCap);
  const toFact = ([k, v]: [string, string]): KeyFact => ({
    key: `key:${k}`,
    label: keyLabel(k),
    value: v,
  });
  return {
    visible: [...head, ...chips.visible.map(toFact)],
    overflow: chips.overflow.map(toFact),
  };
}

/** A markdown list marker at the start of a line: `1.`, `2)`, `-`, `*`, `+`, `•`. */
const FACT_MARKER = /^\s*(?:\d+[.)]|[-*+•])\s+/;

/**
 * Merlin's key facts as the rows the brief renders (kanban cf081c19 — Jess,
 * 2026-10-01: a few key points to triage from, each clickable to see what
 * Merlin checked). Newer filings send `[{fact, basis}]`; the first day's
 * filings sent a markdown-bullet string, which becomes facts with no basis,
 * one per line. Blank facts drop; absence is an empty list.
 */
export function keyFactItems(filing: Pick<ThreadInvestigation, 'key_facts'>): ThreadKeyFact[] {
  const raw = filing.key_facts;
  if (!raw) return [];
  if (typeof raw === 'string') {
    return raw
      .split('\n')
      .map((line) => line.replace(FACT_MARKER, '').trim())
      .filter((fact) => fact.length > 0)
      .map((fact) => ({ fact, basis: '', evidence: [] }));
  }
  return raw
    .map((item) => ({
      fact: (item.fact ?? '').trim(),
      basis: (item.basis ?? '').trim(),
      label: (item.label ?? '').trim(),
      headline: (item.headline ?? '').trim(),
      status: item.status ?? '',
      evidence: evidenceRefs(item.evidence),
    }))
    .filter((item) => item.fact.length > 0);
}

/* ---------------------------------------------------------------- */
/* Why a Do next step (kanban 1af16b1c)                               */
/* ---------------------------------------------------------------- */

export interface StepEvidence {
  /** What Merlin checked that makes it the step; '' when it gave only refs. */
  basis: string;
  evidence: EvidenceRef[];
}

/**
 * Why a Do next step, from the filing's `step_evidence`: the entries for
 * the recommended-action numbers the step stands for (its own, or the ones
 * folded into a proposed item), their bases joined and their refs merged.
 * Null when Merlin filed nothing for it — the step then opens nothing,
 * and a filing from before the field renders as it always did.
 */
export function stepEvidenceFor(
  filing: Pick<ThreadInvestigation, 'step_evidence'>,
  step: Pick<DoNextStep, 'recommendedNos'>,
): StepEvidence | null {
  const entries = Array.isArray(filing.step_evidence) ? filing.step_evidence : [];
  const mine = step.recommendedNos
    .map((no) => entries.find((e) => e.step === no))
    .filter((e): e is NonNullable<typeof e> => !!e);
  if (mine.length === 0) return null;
  const basis = mine
    .map((e) => (e.basis ?? '').trim())
    .filter(Boolean)
    .join(' ');
  const evidence = evidenceRefs(mine.flatMap((e) => (Array.isArray(e.evidence) ? e.evidence : [])));
  if (!basis && evidence.length === 0) return null;
  return { basis, evidence };
}

/** The cause-chain steps that drive a Do next step, oldest first — the
 *  slice of "how we got here" behind that one action. */
export function chainStepsDriving(
  chain: ReadonlyArray<ChainStep>,
  stepKey: string,
): Array<{ key: string; when: string; title: string; state: ChainStep['state'] }> {
  return chain
    .filter((s) => s.drives.some((d) => d.key === stepKey))
    .map((s) => ({ key: s.key, when: s.when, title: s.title, state: s.state }));
}

/* ---------------------------------------------------------------- */
/* Do next                                                           */
/* ---------------------------------------------------------------- */

export interface DoNextStep {
  key: string;
  /** Plain text for a proposed item; markdown for Merlin's recommended action. */
  text: string;
  format: 'plain' | 'markdown';
  /** Whose desk a proposed item sits on; null for the recommended action. */
  owner: CaseTaskOwner | null;
  done: boolean;
  /** Who Merlin names for it ("Emily/Roblyn"), when it does; null otherwise. */
  who: string | null;
  /** The recommended-action items (1-based) this step stands for — its own
   *  number, or the ones folded into a proposed item that already says it. */
  recommendedNos: number[];
}

/** A markdown list marker at the start of a line: `1.`, `2)`, `-`, `*`, `+`. */
const LIST_ITEM = /^\s*(?:\d+[.)]|[-*+])\s+(.*)$/;

/**
 * The recommended action as separate items when Merlin filed it as ONE
 * markdown list (kanban 0fb8105b: the filing now asks for a numbered,
 * verb-first list). Every non-blank line must be a list item or an indented
 * continuation of the one before it; anything else — prose, a lead-in line,
 * a single item — returns null and the action renders as one markdown step,
 * where a nested list still displays correctly.
 */
export function splitRecommendedAction(action: string): string[] | null {
  const items: string[] = [];
  for (const raw of action.split('\n')) {
    if (!raw.trim()) continue;
    const match = LIST_ITEM.exec(raw);
    if (match) {
      items.push(match[1].trim());
    } else if (items.length > 0 && /^\s/.test(raw)) {
      items[items.length - 1] = `${items[items.length - 1]} ${raw.trim()}`;
    } else {
      return null;
    }
  }
  return items.length >= 2 ? items : null;
}

/**
 * One list: the proposed case-file items, then the filing's recommended
 * action — one step per item when it is a list, else one markdown step.
 * A recommended item that restates a proposed item is not listed twice: it
 * folds into that item (case round, finding 9: 16 steps where 11 to 16
 * restated 1 to 10), which takes the owner Merlin named for it. Open steps
 * come first; done and dropped ones last (the screen folds them away).
 * Pass `proposal` = null once a real case file exists — its checklist
 * renders on the case file, not here.
 */
export function doNextSteps(
  filing: Pick<ThreadInvestigation, 'recommended_action'>,
  proposal: Pick<ThreadCaseFileProposal, 'items'> | null,
  /** `fold: false` keeps every step where it was filed, nothing folded and
   *  nothing reordered: the layout D desk view (stepsForDesks/doNextByDesk)
   *  does its own de-duplication and numbers steps by their filed place. */
  { fold = true }: { fold?: boolean } = {},
): DoNextStep[] {
  const items: ThreadCaseFileProposal['items'] = proposal?.items ?? [];
  const steps: DoNextStep[] = items.map((item) => ({
    key: `item:${item.id}`,
    text: item.label,
    format: 'plain',
    owner: item.owner,
    // A dropped (cancelled) item is no longer to do either.
    done: item.status !== 'open',
    who: null,
    recommendedNos: [],
  }));
  const proposed = steps.slice();
  const action = filing.recommended_action.trim();
  const split = action ? splitRecommendedAction(action) : null;
  const recommended = split ?? (action ? [action] : []);
  recommended.forEach((text, i) => {
    const no = i + 1;
    const same = split && fold ? restatedItem(text, proposed.map((p) => p.text)) : -1;
    if (same >= 0) {
      proposed[same].recommendedNos.push(no);
      proposed[same].who ??= stepOwner(text);
      return;
    }
    steps.push({
      key: split ? `recommended:${no}` : 'recommended',
      text,
      format: 'markdown',
      owner: null,
      done: false,
      who: split ? stepOwner(text) : null,
      recommendedNos: [no],
    });
  });
  if (!fold) return steps;
  return [...steps.filter((s) => !s.done), ...steps.filter((s) => s.done)];
}

/* ---------------------------------------------------------------- */
/* Details (folded away)                                             */
/* ---------------------------------------------------------------- */

/**
 * What the collapsed "Details" holds, so the summary line can name it
 * honestly ("How Merlin traced it · where it stands · 2 answered · sources").
 */
export function briefDetailParts(
  filing: Pick<ThreadInvestigation, 'traced' | 'checked'>,
  proposal: Pick<
    ThreadCaseFileProposal,
    'current_state' | 'findings_notes' | 'answered_questions'
  > | null,
): string[] {
  const parts: string[] = [];
  if (filing.traced.trim()) parts.push('how Merlin traced it');
  if (proposal?.current_state.trim()) parts.push('where it stands');
  if (proposal && proposal.findings_notes.length > 0) parts.push('Merlin’s notes');
  if (proposal && proposal.answered_questions.length > 0) {
    const n = proposal.answered_questions.length;
    parts.push(`${n} question${n === 1 ? '' : 's'} already answered`);
  }
  if (filing.checked.length > 0) parts.push('sources checked');
  return parts;
}

/* ---------------------------------------------------------------- */
/* How we got here (the cause chain)                                 */
/* ---------------------------------------------------------------- */

export interface ChainStep {
  key: string;
  when: string;
  title: string;
  detail: string;
  state: ThreadCauseStep['state'];
  /** The key fact this step rests on, with what Merlin checked for it. */
  fact: ThreadKeyFact | null;
  /** The do-next steps this one drives, numbered as the Do next list shows
   *  them, with each step's key so a step can find the links that drive it. */
  drives: Array<{ no: number; text: string; key: string }>;
}

/**
 * Merlin's cause chain resolved against the brief: each step's fact, and the
 * do-next items it drives with the number they carry in the Do next list.
 * `drives` counts into Merlin's recommended action; each number is mapped
 * to the Do next step that stands for it (its own, or the proposed item it
 * restates), numbered as the list shows it. A
 * reference that points nowhere is dropped. Empty when the filing has no
 * chain (filings before it; the brief then shows the key-fact rows).
 */
export function causeChainSteps(
  filing: Pick<ThreadInvestigation, 'cause_chain' | 'key_facts'>,
  doNext: DoNextStep[],
): ChainStep[] {
  const chain = Array.isArray(filing.cause_chain) ? filing.cause_chain : [];
  const facts = keyFactItems(filing);
  // Recommended item n → the step that now stands for it, numbered as shown.
  const forRecommended = (n: number) => {
    const i = doNext.findIndex((step) => step.recommendedNos.includes(n));
    return i < 0 ? undefined : { step: doNext[i], no: i + 1 };
  };
  return chain
    .filter((s) => (s.step ?? '').trim().length > 0)
    .map((s, i) => ({
      key: `step:${i}`,
      when: (s.when ?? '').trim(),
      title: s.step.trim(),
      detail: (s.detail ?? '').trim(),
      state: s.state === 'now' || s.state === 'next' ? s.state : 'past',
      fact: s.fact != null && s.fact >= 1 ? (facts[s.fact - 1] ?? null) : null,
      drives: (s.drives ?? [])
        .map((n) => forRecommended(n))
        .filter((r): r is { step: DoNextStep; no: number } => r !== undefined)
        .filter((r, j, all) => all.findIndex((o) => o.no === r.no) === j)
        .map(({ step, no }) => ({ no, text: step.text, key: step.key })),
    }));
}

/** The step the chain opens on: the one marked now, else the last that happened. */
export function defaultChainStep(steps: ChainStep[]): string | null {
  const now = steps.find((s) => s.state === 'now');
  if (now) return now.key;
  const past = steps.filter((s) => s.state === 'past');
  return (past[past.length - 1] ?? steps[0])?.key ?? null;
}

/** Key facts no chain step points at — still listed, under the chain. */
export function factsOffTheChain(
  filing: Pick<ThreadInvestigation, 'key_facts'>,
  steps: ChainStep[],
): ThreadKeyFact[] {
  const used = new Set(steps.map((s) => s.fact?.fact).filter(Boolean));
  return keyFactItems(filing).filter((f) => !used.has(f.fact));
}

/* ---------------------------------------------------------------- */
/* The situation (tiles) and Do next by desk — layout D              */
/* ---------------------------------------------------------------- */

export type TileStatus = 'problem' | 'watch' | 'ok' | 'none';

export interface SituationTile {
  key: string;
  /** The topic in a word or two ("Stock"); blank when Merlin gave none. */
  label: string;
  /** The number or state the tile leads with. */
  headline: string;
  status: TileStatus;
  fact: string;
  /** What Merlin checked for it; blank on a fact filed without one. */
  basis: string;
  /** The identifiers Merlin read for it (kanban 1af16b1c); none before it. */
  evidence: EvidenceRef[];
  /** The cause-chain steps that rest on this fact, oldest first. */
  steps: Array<{ key: string; when: string; title: string; state: ChainStep['state'] }>;
}

const TILE_STATUSES: readonly TileStatus[] = ['problem', 'watch', 'ok'];

/**
 * Merlin's key facts as situation tiles (kanban 538d6a0d; Jess picked layout
 * D on the layouts review). Empty unless the filing gave at least one fact a
 * headline: a filing from before the tiles keeps the chain and fact rows it
 * was written for, since a full sentence does not read as a tile. A fact
 * without a headline in a newer filing leads with the fact itself.
 */
export function situationTiles(
  filing: Pick<ThreadInvestigation, 'key_facts'>,
  chain: ChainStep[],
): SituationTile[] {
  const facts = keyFactItems(filing);
  if (!facts.some((f) => (f.headline ?? '').length > 0)) return [];
  return facts.map((f, i) => {
    const steps = chain.filter((s) => s.fact?.fact === f.fact);
    const status = TILE_STATUSES.find((known) => known === f.status) ?? 'none';
    return {
      key: `tile:${i}`,
      label: f.label ?? '',
      headline: f.headline || f.fact,
      status,
      fact: f.fact,
      basis: f.basis,
      evidence: evidenceRefs(f.evidence),
      steps: steps.map((s) => ({ key: s.key, when: s.when, title: s.title, state: s.state })),
    };
  });
}

export interface DeskGroup {
  /** The desk as Merlin named it ("Jess", "Roblyn / Emily"); '' = none named. */
  desk: string;
  /** True when the desk names the person reading the brief. */
  mine: boolean;
  /** True when the reader is named anywhere in the desk ("Jess / Jake",
   *  "Jess to trigger"): nobody to draft a request to but themselves. */
  namesReader: boolean;
  /** True for the customer's desk (proposed items owned by the customer):
   *  never asked from the door — quality goes through the account's sales
   *  rep — so the pane shows a recommendation, not an Ask (kanban d3915b86). */
  customer: boolean;
  steps: Array<{ no: number; step: DoNextStep; text: string }>;
}

const PROPOSED_DESK: Record<CaseTaskOwner, string> = {
  us: 'Us',
  colleague: 'A colleague',
  customer: 'The customer',
};

/**
 * The Do next list grouped by whose desk each step sits on. Numbers are the
 * ones the flat list carries, so a tile's "what to do about it" still points
 * at the right step. The reader's own desk comes first, then desks in the
 * order Merlin first named them, then the steps with no desk named.
 */
export function doNextByDesk(doNext: DoNextStep[], viewerName: string | null): DeskGroup[] {
  const viewerWords = (viewerName ?? '').toLowerCase().split(/[^a-z]+/).filter(Boolean);
  const first = viewerWords[0] ?? '';
  const groups = new Map<string, DeskGroup>();
  doNext.forEach((step, i) => {
    let desk = '';
    let text = step.text;
    if (step.owner) {
      desk = PROPOSED_DESK[step.owner];
    } else {
      ({ desk, text } = splitDesk(step.text));
    }
    const id = desk.toLowerCase();
    const group = groups.get(id) ?? {
      desk,
      // Only a desk that is the reader's own name: "Emily/Roblyn, Jess to
      // trigger" and "Jess to trigger" are their own groups, said as filed.
      mine:
        first.length >= 3 &&
        id.split(/[^a-z]+/)[0] === first &&
        id.split(/[^a-z]+/).filter(Boolean).every((word) => viewerWords.includes(word)),
      namesReader: first.length >= 3 && id.split(/[^a-z]+/).includes(first),
      // The customer's desk: a proposed item the customer owns, or a
      // recommended step Merlin ended with "(customer)".
      customer: step.owner === 'customer' || isCustomerDesk(desk),
      steps: [],
    };
    group.steps.push({ no: i + 1, step, text });
    groups.set(id, group);
  });
  const all = [...groups.values()];
  return [
    ...all.filter((g) => g.mine),
    ...all.filter((g) => !g.mine && g.desk),
    ...all.filter((g) => !g.mine && !g.desk),
  ];
}

/**
 * The steps the desk groups show. Merlin files the same work twice: as the
 * proposed case-file items and as its recommended action, and a reader of
 * the by-desk view met every step two times (explorer run 1, kanban
 * 538d6a0d). When the recommended action is a list, it alone is grouped —
 * it names a desk per step; the proposed items still land on the case when
 * the investigation is opened. Otherwise every step is kept.
 */
export function stepsForDesks(doNext: DoNextStep[]): DoNextStep[] {
  const listed = doNext.filter((s) => s.key.startsWith('recommended:'));
  return listed.length > 0 ? listed : doNext;
}

/**
 * The Do next step a case's outstanding item restates, numbered the way
 * the desk view numbers it (its place in stepsForDesks), with the desk the
 * step names: an ask sent from the case file's row still finds Merlin's
 * pre-written request for that step, and a row whose own label names no
 * desk can borrow the step's (QT-10054: the proposed item "Clear SHERW01
 * CreditHold Y…" names nobody; Do next step 9 says "(Laura Caswell)").
 * Merlin files the same work twice — as the proposed checklist item and
 * as its recommended action — in different words; the restating rule is
 * the one doNextSteps folds them with. Null when no step matches.
 */
export function stepForTask(
  label: string,
  doNext: DoNextStep[],
): { no: number; desk: string; text: string } | null {
  const item = splitDesk(label).text;
  if (!item.trim()) return null;
  const steps = stepsForDesks(doNext);
  const at = steps.findIndex((step) => restatedItem(splitDesk(step.text).text, [item]) === 0);
  if (at < 0) return null;
  const { desk, text } = splitDesk(steps[at].text);
  return { no: at + 1, desk, text };
}

/** The number alone (see stepForTask). */
export function stepNoForTask(label: string, doNext: DoNextStep[]): number | null {
  return stepForTask(label, doNext)?.no ?? null;
}

/* ---------------------------------------------------------------- */
/* Do next meets the asks: what is already out with each desk        */
/* ---------------------------------------------------------------- */

/** Where an ask to a desk stands, in the reader's terms. */
export type DeskAskState = 'draft' | 'waiting' | 'replied';

export interface DeskAsk {
  id: string;
  /** Who it went to, as the ask names them. */
  to: string;
  title: string;
  state: DeskAskState;
  /** When it was sent (draft, waiting) or answered (replied). ISO. */
  at: string;
  /** A reply the reader has not opened yet. */
  unseen: boolean;
  /** Reminders already sent while waiting. */
  followups: number;
}

const ASK_STATE: Partial<Record<DoorDataRequestRead['status'], DeskAskState>> = {
  proposed: 'draft',
  open: 'waiting',
  answered: 'replied',
};

/** Name words of three letters or more; "AR/Laura Caswell" -> laura, caswell. */
function nameWords(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((word) => word.length >= 3);
}

/**
 * The asks already out with a desk (owner feedback on review ck3pib,
 * 2026-10-05: combine Do next with "a request sent, a message sent, awaiting
 * reply"). An ask belongs to a desk when a word of the person it went to is a
 * word of the desk, the rule the door uses to match a person to a queue, so
 * "Emily/Roblyn" carries asks to Emily Adams and to Roblyn Jericho. Cancelled
 * and closed asks are left out. Drafts first, then waiting, then replied;
 * newest first within each.
 */
export function asksForDesk(desk: string, asks: DoorDataRequestRead[]): DeskAsk[] {
  const words = new Set(nameWords(desk));
  if (words.size === 0) return [];
  const order: DeskAskState[] = ['draft', 'waiting', 'replied'];
  return asks
    .flatMap((ask): DeskAsk[] => {
      const state = ASK_STATE[ask.status];
      if (!state || !nameWords(ask.target_name).some((word) => words.has(word))) return [];
      return [
        {
          id: ask.id,
          to: ask.target_name,
          title: ask.question_title,
          state,
          at: state === 'replied' ? (ask.answered_at ?? ask.created_at) : ask.created_at,
          unseen: state === 'replied' && ask.answer_unseen,
          followups: ask.followup_count,
        },
      ];
    })
    .sort((a, b) => order.indexOf(a.state) - order.indexOf(b.state) || b.at.localeCompare(a.at));
}

/* ---------------------------------------------------------------- */
/* The asks tied to a desk's steps (kanban d3ec3001)                  */
/* ---------------------------------------------------------------- */

export type DeskAskRecipientState = 'waiting' | 'handed' | 'replied' | 'told' | 'withdrawn' | 'closed';

export interface DeskAskRecipient {
  id: string;
  name: string;
  state: DeskAskRecipientState;
  /** Replied at (replied), else null. ISO. */
  repliedAt: string | null;
  /** Who answered first, on a row that was stood down. */
  toldBy: string | null;
  unseen: boolean;
  followups: number;
  lastFollowupAt: string | null;
  /** How they were chosen: named / role:… / team:… / customer_relay. */
  source: string;
  request: DoorDataRequestRead;
}

/** `handed`: a pass-along (kanban 196e9d9b) — out with the desk that owns it,
 *  nobody waiting on a reply. */
export type DeskAskGroupState = 'waiting' | 'handed' | 'replied' | 'withdrawn' | 'done';

/** One ask as sent — every recipient of it, as one pill on the desk. */
export interface DeskAskGroup {
  id: string;
  stepNo: number | null;
  title: string;
  body: string;
  /** When it was sent. ISO. */
  sentAt: string;
  recipients: DeskAskRecipient[];
  state: DeskAskGroupState;
  unseen: boolean;
}

const RECIPIENT_STATE: Partial<Record<DoorDataRequestRead['status'], DeskAskRecipientState>> = {
  open: 'waiting',
  answered: 'replied',
  closed: 'closed',
  cancelled: 'withdrawn',
};

function recipientOf(r: DoorDataRequestRead): DeskAskRecipient {
  const base = RECIPIENT_STATE[r.status] ?? 'closed';
  const state: DeskAskRecipientState = isHandedPassAlong(r)
    ? 'handed'
    : base === 'withdrawn' && r.peer_answered_by
      ? 'told'
      : base;
  return {
    id: r.id,
    name: r.target_name,
    state,
    repliedAt: state === 'replied' ? (r.answered_at ?? r.created_at) : null,
    toldBy: state === 'told' ? (r.peer_answered_by ?? null) : null,
    unseen: state === 'replied' && r.answer_unseen,
    followups: r.followup_count,
    lastFollowupAt: r.last_followup_at ?? null,
    source: r.recipient_source ?? 'named',
    request: r,
  };
}

/** A row born on a thread's Do next (newer backends stamp thread_id + step_no). */
function onStep(r: DoorDataRequestRead, threadId: string, stepNos: Set<number>): boolean {
  return r.thread_id === threadId && r.step_no != null && stepNos.has(r.step_no);
}

/**
 * Merlin's unsent drafts for a desk's steps: what "Ask <desk>" opens with.
 * Only rows tied to the step count — a draft filed from chat stays a card.
 */
export function deskDrafts(
  group: { steps: ReadonlyArray<{ no: number }> },
  thread: { id: string },
  asks: DoorDataRequestRead[],
): DoorDataRequestRead[] {
  const nos = new Set(group.steps.map((s) => s.no));
  return asks
    .filter((r) => isDraft(r) && onStep(r, thread.id, nos))
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
}

/**
 * The drafts a Do next desk's "Ask <desk>" button sends: only desks that
 * show that button (a named desk that is not the reader's own). A step
 * draft on the reader's own desk, or on a step no group shows, has no
 * button, so the asks list keeps it with its Send (UX round 2026-10-27:
 * the draft to Rich on QT-10062 was in neither place).
 */
export function deskOwnedDraftIds(
  groups: ReadonlyArray<Pick<DeskGroup, 'desk' | 'mine' | 'steps'>>,
  thread: { id: string },
  asks: DoorDataRequestRead[],
): Set<string> {
  const ids = new Set<string>();
  for (const g of groups) {
    if (g.mine || !g.desk) continue;
    for (const d of deskDrafts(g, thread, asks)) ids.add(d.id);
  }
  return ids;
}

const GROUP_ORDER: DeskAskGroupState[] = ['waiting', 'handed', 'replied', 'done', 'withdrawn'];

/**
 * Sent asks as groups, one per send (every recipient of it together),
 * newest first within a state: waiting, then replied, then done, then
 * withdrawn. Shared by the Do next desks and the case file's outstanding
 * items, which pick their own rows first.
 */
export function groupSentAsks(rows: DoorDataRequestRead[]): DeskAskGroup[] {
  const byGroup = new Map<string, DoorDataRequestRead[]>();
  rows.forEach((r) => {
    const key = r.broadcast_group_id ?? r.id;
    byGroup.set(key, [...(byGroup.get(key) ?? []), r]);
  });
  const groups: DeskAskGroup[] = [...byGroup.entries()].map(([id, rows]) => {
    const recipients = rows
      .map(recipientOf)
      .sort((a, b) => a.name.localeCompare(b.name));
    const states = recipients.map((r) => r.state);
    const state: DeskAskGroupState = states.includes('replied')
      ? 'replied'
      : states.includes('waiting')
        ? 'waiting'
        : states.includes('handed')
          ? 'handed'
          : states.every((s) => s === 'withdrawn')
            ? 'withdrawn'
            : 'done';
    const first = rows.slice().sort((a, b) => a.created_at.localeCompare(b.created_at))[0];
    const sentAt = rows
      .map((r) => r.sent_at ?? r.created_at)
      .sort()[0];
    return {
      id,
      stepNo: first.step_no ?? null,
      title: first.question_title,
      body: first.question_body,
      sentAt,
      recipients,
      state,
      unseen: recipients.some((r) => r.unseen),
    };
  });
  return groups.sort(
    (a, b) => GROUP_ORDER.indexOf(a.state) - GROUP_ORDER.indexOf(b.state) || b.sentAt.localeCompare(a.sentAt),
  );
}

/**
 * The asks already SENT for a desk, one group per send. Rows are matched to
 * the desk by the step they were filed for; rows from before that (no
 * thread_id) fall back to the name-word rule asksForDesk uses, each as its
 * own group.
 */
export function deskAskGroups(
  group: Pick<DeskGroup, 'desk' | 'steps'>,
  thread: { id: string },
  asks: DoorDataRequestRead[],
): DeskAskGroup[] {
  const nos = new Set(group.steps.map((s) => s.no));
  const words = new Set(nameWords(group.desk));
  return groupSentAsks(
    asks.filter(
      (r) =>
        !isDraft(r) &&
        (onStep(r, thread.id, nos) ||
          (!r.thread_id && words.size > 0 && nameWords(r.target_name).some((w) => words.has(w)))),
    ),
  );
}

const firstName = (name: string) => name.split(' ')[0] || name;

/** "today 2:41 PM" / "Oct 5, 9:10 AM" for the pill and the status card. */
export function whenLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return `today ${time}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
}

/**
 * What the pill says: "Asked Cliff, waiting · today 2:41 PM", "Asked 3
 * people, waiting · …, reminded once", "Replied — Emily · today 3:12 PM ·
 * Cliff, Roblyn told", "Withdrawn".
 */
export function pillLabel(g: DeskAskGroup, now: Date = new Date()): string {
  const live = g.recipients.filter((r) => r.state !== 'withdrawn');
  const replied = live.filter((r) => r.state === 'replied');
  const told = live.filter((r) => r.state === 'told');
  if (g.state === 'replied') {
    const who = replied.map((r) => firstName(r.name)).join(', ');
    const at = replied[0]?.repliedAt ? ` · ${whenLabel(replied[0].repliedAt, now)}` : '';
    const toldPart = told.length ? ` · ${told.map((r) => firstName(r.name)).join(', ')} told` : '';
    return `Replied — ${who}${at}${toldPart}`;
  }
  if (g.state === 'waiting') {
    const waiting = live.filter((r) => r.state === 'waiting');
    const who = waiting.length === 1 ? firstName(waiting[0].name) : `${waiting.length} people`;
    const reminders = Math.max(0, ...waiting.map((r) => r.followups - 1));
    const reminded = reminders > 0 ? `, reminded ${reminders === 1 ? 'once' : `${reminders} times`}` : '';
    return `Asked ${who}, waiting · ${whenLabel(g.sentAt, now)}${reminded}`;
  }
  if (g.state === 'handed') {
    // A pass-along: theirs now, and what Merlin's check did with it.
    const handed = live.filter((r) => r.state === 'handed');
    const who = handed.length === 1 ? firstName(handed[0].name) : `${handed.length} people`;
    const gate = handed[0]?.request.handoff;
    const status = gate ? handoffWords(gate, who).split(' · ')[0] : 'delivered';
    return `Passed to ${who} · ${status} · ${whenLabel(g.sentAt, now)}`;
  }
  if (g.state === 'withdrawn') return `Withdrawn · ${whenLabel(g.sentAt, now)}`;
  return `Closed · ${whenLabel(g.sentAt, now)}`;
}
